import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { JOYSPOT_QUIZ_URL } from "@/lib/constants";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/joyspot", label: "Find Your JOYspot™" },
  { to: "/about", label: "About" },
  { to: "/work-with-me", label: "Work With Me" },
  { to: "/books", label: "Books" },
  { to: "/this-curious-life", label: "This Curious Life" },
  { to: "/speaking", label: "Speaking" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl border-b transition-all duration-300"
      style={{
        backgroundColor: isScrolled 
          ? 'rgba(250, 250, 248, 0.95)' 
          : 'rgba(250, 250, 248, 0.85)',
        backgroundImage: isScrolled
          ? 'linear-gradient(to bottom, rgba(246, 160, 65, 0.08), rgba(246, 160, 65, 0.05))'
          : 'none',
        borderBottomColor: isScrolled
          ? 'rgba(246, 160, 65, 0.15)'
          : 'rgba(21, 158, 154, 0.15)'
      }}
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-10 h-20 flex items-center justify-between gap-8">
        {/* Logo - larger and more confident */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 flex-shrink-0">
          <span className="font-display text-[26px] tracking-tight leading-none">
            Blueprint<span className="text-[#FF5A4F]">4</span>Joy
            <span className="text-[#159E9A] align-super text-[11px] ml-0.5">®</span>
          </span>
        </Link>

        {/* Navigation - spacious and editorial */}
        <nav className="hidden lg:flex items-center gap-8 xl:gap-10 flex-1 justify-center" data-testid="primary-nav">
          {LINKS.slice(1).map((l) =>
            l.to === "/joyspot" ? (
              <a
                key={l.to}
                href={JOYSPOT_QUIZ_URL}
                data-testid="nav-link-joyspot"
                className="nav-link text-[13px] tracking-[0.02em] font-medium text-[#1C1917]/80 hover:text-[#1C1917] transition-colors text-center leading-[1.3] max-w-[90px]"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `nav-link text-[13px] tracking-[0.02em] font-medium text-[#1C1917]/80 hover:text-[#1C1917] transition-colors text-center leading-[1.3] max-w-[90px] ${
                    isActive ? "text-[#1C1917]" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA - refined and premium */}
        <div className="hidden lg:flex items-center flex-shrink-0">
          <a
            href={JOYSPOT_QUIZ_URL}
            data-testid="header-cta-joyspot"
            className="inline-flex items-center gap-2 bg-[#FF5A4F] text-white rounded-full px-5 py-2 text-[13px] font-medium tracking-wide hover:bg-[#e8463c] transition-all hover:shadow-sm whitespace-nowrap"
          >
            Find Your JOYspot™
          </a>
        </div>

        {/* Mobile menu toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 -mr-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#159E9A]/15 bg-[#FAFAF8]"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) =>
              l.to === "/joyspot" ? (
                <a
                  key={l.to}
                  href={JOYSPOT_QUIZ_URL}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-link-joyspot"
                  className="font-display text-2xl text-[#1C1917]"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${l.to.replace("/", "") || "home"}`}
                  className="font-display text-2xl text-[#1C1917]"
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              href={JOYSPOT_QUIZ_URL}
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-joyspot"
              className="mt-2 bg-[#FF5A4F] text-white rounded-full px-5 py-3 text-center font-medium"
            >
              Find Your JOYspot™ →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
